import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mic, MicOff, Sparkles, Save, Trash2, FileText, Loader2, Copy, Check, Globe } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { NOTES_LANGUAGES, detectDefaultNotesLanguage, persistNotesLanguage } from "../../data/notes_languages";

function getRecognition(lang) {
  if (typeof window === "undefined") return null;
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SR) return null;
  const r = new SR();
  r.continuous = true;
  r.interimResults = true;
  r.lang = lang || "en-GB";
  return r;
}

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-GB", {
    dateStyle: "medium", timeStyle: "short",
  });
}

export default function MeetingNotes() {
  const { user } = useAuth();
  const [supported, setSupported] = useState(true);
  const [recording, setRecording] = useState(false);
  const [interim, setInterim] = useState("");
  const [transcript, setTranscript] = useState("");
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [summarising, setSummarising] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [history, setHistory] = useState([]);
  const [lang, setLang] = useState(() => detectDefaultNotesLanguage());
  const recRef = useRef(null);
  const restartRef = useRef(true);

  useEffect(() => {
    const r = getRecognition(lang);
    if (!r) {
      setSupported(false);
      return;
    }
    r.onresult = (e) => {
      let finalText = "";
      let interimText = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const res = e.results[i];
        if (res.isFinal) finalText += res[0].transcript + " ";
        else interimText += res[0].transcript;
      }
      if (finalText) setTranscript((t) => (t + finalText).replace(/\s+/g, " ").trim() + " ");
      setInterim(interimText);
    };
    r.onend = () => {
      // Browsers stop recognition after silence — auto-restart while user wants to record.
      if (restartRef.current) {
        try { r.start(); } catch (e) { /* ignore */ }
      }
    };
    r.onerror = (e) => {
      if (e.error === "not-allowed" || e.error === "service-not-allowed") {
        toast.error("Microphone permission denied. Allow it in your browser settings.");
        setRecording(false);
        restartRef.current = false;
      }
    };
    recRef.current = r;
    return () => {
      restartRef.current = false;
      try { r.stop(); } catch (e) { /* ignore */ }
    };
  }, [lang]);

  const changeLang = (code) => {
    setLang(code);
    persistNotesLanguage(code);
    if (recording) {
      restartRef.current = false;
      try { recRef.current?.stop(); } catch (e) { /* ignore */ }
      setTimeout(() => {
        restartRef.current = true;
        try { recRef.current?.start(); } catch (e) { /* ignore */ }
      }, 300);
    }
  };

  useEffect(() => {
    if (!user) return;
    api.get("/platform/meeting-notes/mine").then((r) => setHistory(r.data.notes || [])).catch(() => {});
  }, [user]);

  const start = () => {
    if (!recRef.current) return;
    restartRef.current = true;
    try {
      recRef.current.start();
      setRecording(true);
    } catch (e) { /* already started */ }
  };
  const stop = () => {
    restartRef.current = false;
    try { recRef.current?.stop(); } catch (e) { /* ignore */ }
    setRecording(false);
    setInterim("");
  };
  const clearAll = () => {
    if (!window.confirm("Clear current transcript and summary?")) return;
    stop();
    setTranscript("");
    setInterim("");
    setSummary("");
    setSavedId(null);
    setTitle("");
  };

  const summarise = async () => {
    if (!transcript.trim() || transcript.trim().length < 20) {
      toast.error("Need at least a few sentences of transcript first.");
      return;
    }
    setSummarising(true);
    setSummary("");
    try {
      const res = await api.post("/platform/meeting-notes/summarise", {
        transcript: transcript.trim(),
        title: title.trim() || null,
      });
      setSummary(res.data.summary_md || "");
      if (res.data.saved_id) {
        setSavedId(res.data.saved_id);
        // refresh history
        const h = await api.get("/platform/meeting-notes/mine");
        setHistory(h.data.notes || []);
      }
      toast.success("Summary generated and saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not generate summary.");
    } finally {
      setSummarising(false);
    }
  };

  const removeNote = async (id) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await api.delete(`/platform/meeting-notes/${id}`);
      setHistory((h) => h.filter((n) => n.note_id !== id));
      toast.success("Note deleted");
    } catch (e) { toast.error("Could not delete"); }
  };

  if (!user) {
    return (
      <div className="px-4 sm:px-8 lg:px-10 py-12 max-w-2xl mx-auto">
        <Link to="/platform" className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy mb-5">
          <ArrowLeft size={14} /> Back to dashboard
        </Link>
        <div className="rounded-xl bg-white border border-navy/10 p-8 text-center shadow-sm">
          <FileText size={28} className="mx-auto text-saffron mb-3" />
          <h1 className="font-display text-3xl text-navy">Login to take notes</h1>
          <p className="mt-2 text-navy/65">AI Meeting Notes is for logged-in members so we can save your transcripts privately.</p>
          <Link to="/login" className="inline-block mt-5 px-5 py-2.5 bg-navy text-pearl rounded-md text-sm hover:bg-navy-light">Login</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-8 lg:px-10 py-8 max-w-[1300px] mx-auto" data-testid="meeting-notes-page">
      <Link to="/platform" className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy mb-5">
        <ArrowLeft size={14} /> Back to dashboard
      </Link>
      <p className="text-[10px] uppercase tracking-[0.25em] text-saffron">AI Meeting Notes · Beta</p>
      <h1 className="font-display text-4xl text-navy">Speak. Listen. Summarise.</h1>
      <p className="mt-2 text-navy/65 max-w-2xl">
        Free in-browser transcription using your device's mic, no recording leaves your computer
        until you click "Generate summary". Then Gemini turns the transcript into tidy minutes,
        action items and pull-quotes. Best in Chrome, Edge or Safari.
      </p>

      {!supported && (
        <div className="mt-6 rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-800" data-testid="meeting-notes-unsupported">
          ⚠️ Your browser does not support live speech recognition. Please use Chrome, Edge or Safari
          for this feature. (Firefox does not yet support the Web Speech API.)
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Transcript card */}
          <div className="bg-white border border-navy/10 rounded-xl p-6 shadow-sm" data-testid="transcript-card">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Title (optional, e.g. Sunday Dhamma study)"
                className="text-lg font-display text-navy bg-transparent border-0 focus:outline-none flex-1 min-w-[180px]"
                data-testid="meeting-notes-title"
                maxLength={160}
              />
              <div className="flex items-center gap-2">
                <div className="hidden sm:inline-flex items-center gap-1 text-xs text-navy/60" title="Recognition language">
                  <Globe size={12} />
                  <select
                    value={lang}
                    onChange={(e) => changeLang(e.target.value)}
                    data-testid="meeting-notes-language"
                    className="bg-transparent border border-navy/15 rounded px-2 py-1 text-xs focus:outline-none focus:border-saffron max-w-[160px]"
                  >
                    {NOTES_LANGUAGES.map((l) => (
                      <option key={l.code} value={l.code}>{l.label}</option>
                    ))}
                  </select>
                </div>
                {!recording ? (
                  <button
                    onClick={start}
                    disabled={!supported}
                    data-testid="meeting-notes-start"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-saffron text-navy rounded-md text-sm hover:bg-saffron/90 disabled:opacity-40 font-medium"
                  >
                    <Mic size={14} /> Start recording
                  </button>
                ) : (
                  <button
                    onClick={stop}
                    data-testid="meeting-notes-stop"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-md text-sm hover:bg-red-600 font-medium"
                  >
                    <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                    <MicOff size={14} /> Stop
                  </button>
                )}
                <button
                  onClick={clearAll}
                  disabled={!transcript && !summary}
                  className="p-2 text-navy/50 hover:text-red-700 disabled:opacity-30"
                  title="Clear"
                  data-testid="meeting-notes-clear"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            <div className="bg-pearl/40 border border-navy/10 rounded-md p-4 min-h-[180px] max-h-[400px] overflow-y-auto text-navy/85 leading-relaxed font-serif text-[15px] whitespace-pre-wrap" data-testid="transcript-area">
              {transcript || (recording ? "" : <span className="italic text-navy/40">Click "Start recording" — speak naturally and your words will appear here. Stop when you're done.</span>)}
              {interim && <span className="text-navy/40 italic">{interim}</span>}
            </div>
            <div className="flex items-center justify-between mt-3 text-xs text-navy/55">
              <span>{transcript.split(/\s+/).filter(Boolean).length} words captured</span>
              <button
                onClick={summarise}
                disabled={summarising || !transcript.trim() || transcript.trim().length < 20}
                data-testid="meeting-notes-summarise"
                className="inline-flex items-center gap-2 px-4 py-2 bg-navy text-pearl rounded-md text-sm hover:bg-navy-light disabled:opacity-40"
              >
                {summarising ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                {summarising ? "Generating…" : "Generate AI summary"}
              </button>
            </div>
          </div>

          {/* Summary card */}
          {summary && (
            <SummaryCard summary={summary} savedId={savedId} />
          )}
        </div>

        {/* History sidebar */}
        <aside className="bg-white border border-navy/10 rounded-xl p-5 shadow-sm h-fit" data-testid="meeting-notes-history">
          <h3 className="font-display text-lg text-navy mb-3">Your saved notes</h3>
          {history.length === 0 ? (
            <p className="text-sm text-navy/55 italic">No saved notes yet, generate a summary above and it'll appear here.</p>
          ) : (
            <div className="space-y-2">
              {history.map((n) => (
                <NoteRow key={n.note_id} note={n} onDelete={() => removeNote(n.note_id)} />
              ))}
            </div>
          )}
        </aside>
      </div>

      <p className="mt-10 text-xs text-navy/40 italic max-w-2xl">
        🔒 Privacy: speech recognition runs in YOUR browser. Audio never reaches our server. Only
        the typed transcript text is sent when you click "Generate summary". Saved notes are visible
        only to you (admin can recover them if needed).
      </p>
    </div>
  );
}

function NoteRow({ note, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const [full, setFull] = useState(null);
  const open = async () => {
    if (full) { setExpanded(!expanded); return; }
    try {
      const r = await api.get(`/platform/meeting-notes/${note.note_id}`);
      setFull(r.data);
      setExpanded(true);
    } catch (e) { /* ignore */ }
  };
  return (
    <div className="border border-navy/10 rounded-md p-3 bg-pearl/30">
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <button onClick={open} className="text-left w-full" data-testid={`meeting-note-${note.note_id}`}>
            <div className="text-sm font-medium text-navy truncate">{note.title}</div>
            <div className="text-[10px] text-navy/55 mt-0.5">{fmtDate(note.created_at)}</div>
          </button>
        </div>
        <button onClick={onDelete} className="text-red-700/60 hover:text-red-800 p-1" title="Delete">
          <Trash2 size={12} />
        </button>
      </div>
      {expanded && full && (
        <pre className="mt-3 text-xs text-navy/80 whitespace-pre-wrap font-serif leading-relaxed">{full.summary_md}</pre>
      )}
    </div>
  );
}

function SummaryCard({ summary, savedId }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(summary);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) { /* ignore */ }
  };
  return (
    <div className="bg-gradient-to-br from-[#6C4EFF]/8 to-[#2D5BFF]/5 border border-[#6C4EFF]/20 rounded-xl p-6 shadow-sm" data-testid="summary-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-gradient-to-br from-[#6C4EFF] to-[#2D5BFF] text-white rounded-md">
            <Sparkles size={14} />
          </div>
          <h2 className="font-display text-xl text-navy">AI Summary</h2>
          {savedId && <span className="text-[10px] uppercase tracking-widest text-saffron">Saved</span>}
        </div>
        <button onClick={copy} className="inline-flex items-center gap-1.5 text-xs text-navy/60 hover:text-saffron" data-testid="summary-copy">
          {copied ? <Check size={13} /> : <Copy size={13} />}
          {copied ? "Copied!" : "Copy"}
        </button>
      </div>
      <pre className="whitespace-pre-wrap text-navy/85 leading-relaxed font-serif text-[15px]">{summary}</pre>
    </div>
  );
}
